import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Table,
  Tag,
  Space,
  Input,
  Select,
  Form,
  Tooltip,
  Popconfirm,
  Divider,
  Row,
  Col,
} from 'antd';
import Layout from '../../components/Layout';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined,
    UploadOutlined, SearchOutlined, ExclamationCircleOutlined,
    ReloadOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import ImportExcelModal from './ImportExcelModal';
import { habilitationsService } from "../../services/HabilitationsService";
import { modulesService } from "../../services/ModuleService";
import { ACTION_COLORS, ACTION_LABELS, TYPE_ACCES_COLORS, TYPE_ACCES_LABELS } from "../../variables/rbaColors";
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';
import { filtreSansAccents } from '../../utils/rechercheTexte';
import { can, HAB } from '../../variables/constants';

const { Text } = Typography;
const { Option } = Select;

// ── Enum lists ────────────────────────────────────────────────────────────────
const ACTIONS = ['CREATE', 'READ', 'UPDATE', 'DELETE', 'VALIDATE', 'EXPORT'];
const TYPE_ACCES = ['PROPRE', 'ORGANISME', 'GLOBAL'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function Habilitations() {
    const { t } = useTranslation('habilitations');
    /* Qui voit le code. Lu une fois : la session ne change pas en cours d'ecran,
       et le recalculer a chaque rendu ferait croire le contraire. */
    const codeVisible = useMemo(() => can(HAB.HABILITATION_UPDATE), []);

    // ── State ──
    const [habilitations, setHabilitations] = useState([]);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);

    // Filtres
    const [search, setSearch] = useState('');
    const [filterModule, setFilterModule] = useState(null);
    const [filterAction, setFilterAction] = useState(null);

    // Modal CRUD
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [editTarget, setEditTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    // Modal import
    const [importOpen, setImportOpen] = useState(false);

    // ── Data loading ──────────────────────────────────────────────────────────
    const loadHabilitations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await habilitationsService.list();
            setHabilitations(res.data?.data || res.data || []);
        } catch {
            toast.error(i18n.t('habilitations:messages.erreurChargement'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadModules = useCallback(async () => {
        try {
            const res = await modulesService.list();
            setModules(res.data?.data || res.data || []);
        } catch {
            toast.error(i18n.t('habilitations:messages.erreurChargementModules'));
        }
    }, []);

    useEffect(() => {
        loadHabilitations();
        loadModules();
    }, [loadHabilitations, loadModules]);

    // ── Filtrage côté client ──────────────────────────────────────────────────
    const filtered = habilitations.filter((h) => {
        const q = search.toLowerCase();
        /* La recherche ne porte sur le code QUE s'il est visible : sinon, taper un
           code trouverait des lignes sans que rien a l'ecran n'explique pourquoi
           elles correspondent - et confirmerait au passage l'existence du code. */
        const matchSearch =
            !q ||
            (codeVisible && h.codeHabilitation?.toLowerCase().includes(q)) ||
            h.libelleHabilitation?.toLowerCase().includes(q) ||
            h.description?.toLowerCase().includes(q);
        const matchModule = !filterModule || h.moduleTrackingId === filterModule;
        const matchAction = !filterAction || h.action === filterAction;
        return matchSearch && matchModule && matchAction;
    });

    // ── CRUD handlers ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setModalMode('create');
        setEditTarget(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setModalMode('edit');
        setEditTarget(record);
        /* Le code n'est PAS pre-rempli : il n'est plus un champ du formulaire, et
           `update` ne l'a jamais touche cote serveur. */
        form.setFieldsValue({
            libelleHabilitation: record.libelleHabilitation,
            description: record.description,
            action: record.action,
            typeAcces: record.typeAcces,
            moduleTrackingId: record.moduleTrackingId,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            if (modalMode === 'create') {
                await habilitationsService.create(values);
                toast.success(t('messages.creee'));
            } else {
                await habilitationsService.update(editTarget.habilitationTrackingId, values);
                toast.success(t('messages.modifiee'));
            }
            setModalOpen(false);
            loadHabilitations();
        } catch (err) {
            if (err?.errorFields) return; // validation antd
            const msg = err?.response?.data?.message || t('messages.erreurEnregistrement');
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            await habilitationsService.delete(record.habilitationTrackingId);
            toast.success(t('messages.supprimee'));
            loadHabilitations();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('messages.erreurSuppression'));
        }
    };

    // ── Template download ─────────────────────────────────────────────────────
    const handleDownloadTemplate = async () => {
        setTemplateLoading(true);
        try {
            const res = await habilitationsService.downloadTemplate();
            downloadBlob(res.data, 'template-habilitations.xlsx');
        } catch {
            toast.error(t('messages.erreurTemplate'));
        } finally {
            setTemplateLoading(false);
        }
    };

    // ── Import success ────────────────────────────────────────────────────────
    const handleImportSuccess = (n) => {
        toast.success(t('import.succes', { count: n }));
        setImportOpen(false);
        loadHabilitations();
    };

    // ── Données groupées par module pour la table imbriquée ───────────────────
    const groupedData = useMemo(() => {
        const map = {};
        filtered.forEach((h) => {
            const key = h.moduleTrackingId || 'sans-module';
            const label = h.moduleNom || t('liste.sansModule');
            if (!map[key]) map[key] = { key, moduleNom: label, habilitations: [] };
            map[key].habilitations.push(h);
        });
        return Object.values(map);
    }, [filtered, t]);

    // ── Colonnes de la table enfant (habilitations) ───────────────────────────
    const innerColumns = [
        /* LA COLONNE DU CODE N'EST PAS POUR TOUT LE MONDE. Le code est l'autorite
           que les annotations du serveur citent en dur : il sert a qui cable un
           ecran, pas a qui attribue un droit. Il est donc reserve aux porteurs de
           HABILITATION_UPDATE - qui peut modifier le catalogue peut lire ses codes.
           Un administrateur ordinaire ne lit que des libelles.

           `.filter(Boolean)` plutot qu'un `&&` dans le tableau : une colonne
           `false` fait lever le tableau d'Ant. */
        ...(codeVisible ? [{
            title: t('colonnes.code'),
            dataIndex: 'codeHabilitation',
            width: 220,
            sorter: (a, b) => (a.codeHabilitation || '').localeCompare(b.codeHabilitation || ''),
            render: (v) => (
                <Text
                    copyable={{ tooltips: [t('colonnes.copier'), t('colonnes.copie')] }}
                    style={{ fontFamily: 'var(--font-main)', color: '#1a6b3c', fontWeight: 600, fontSize: 12 }}
                >
                    {v}
                </Text>
            ),
        }] : []),
        {
            title: t('colonnes.libelle'),
            dataIndex: 'libelleHabilitation',
            ellipsis: true,
        },
        {
            title: t('colonnes.action'),
            dataIndex: 'action',
            width: 110,
            render: (v) => v ? <Tag color={ACTION_COLORS[v]}>{ACTION_LABELS[v] || v}</Tag> : '-',
        },
        {
            title: t('colonnes.typeAcces'),
            dataIndex: 'typeAcces',
            width: 120,
            render: (v) => v ? <Tag color={TYPE_ACCES_COLORS[v]}>{TYPE_ACCES_LABELS[v] || v}</Tag> : '-',
        },
        {
            title: t('colonnes.description'),
            dataIndex: 'description',
            ellipsis: { showTitle: false },
            render: (v) => (
                <Tooltip title={v} placement="topLeft">
                    <Text type="secondary" style={{ fontSize: 13 }}>{v || '-'}</Text>
                </Tooltip>
            ),
        },
        {
            title: t('colonnes.actions'),
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title={t('actions.modifier')}>
                        <Button
                            type="text" size="small" icon={<EditOutlined />}
                            onClick={() => openEdit(record)}
                            style={{ color: '#1a6b3c' }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('suppression.titre')}
                        description={t('suppression.description')}
                        icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                        onConfirm={() => handleDelete(record)}
                        okText={t('suppression.confirmer')}
                        okButtonProps={{ danger: true }}
                        cancelText={t('suppression.annuler')}
                    >
                        <Tooltip title={t('actions.supprimer')}>
                            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ── Colonnes de la table parente (modules) ────────────────────────────────
    const outerColumns = [
        {
            title: t('colonnes.module'),
            dataIndex: 'moduleNom',
            render: (v, record) => (
                <Space>
                    <Tag color="blue" style={{ fontSize: 13, padding: '2px 10px' }}>{v}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('liste.nbHabilitations', { count: record.habilitations.length })}
                    </Text>
                </Space>
            ),
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Layout title={t('page.titre')}>
            <div style={{ padding: '24px', background: '#f9fafb', minHeight: '100vh' }}>

                {/* ── Header ── */}
                {/* Le tableau affiche `groupedData`, un regroupement par module :
                    sa longueur compterait des modules, pas des habilitations. Le
                    compte utile est donc `filtered.length`, le nombre reel de
                    lignes d'habilitation apres filtres (tout est filtre cote client). */}
                <PageHeader
                    titre={t('page.titre')}
                    icone={<SafetyCertificateOutlined />}
                    compte={filtered.length}
                    unite={t('page.unite')}
                    actions={(
                        <Space wrap>
                            <Button
                                icon={<DownloadOutlined />}
                                loading={templateLoading}
                                onClick={handleDownloadTemplate}
                            >
                                {t('actions.templateExcel')}
                            </Button>
                            <Button
                                icon={<UploadOutlined />}
                                onClick={() => setImportOpen(true)}
                            >
                                {t('actions.importerExcel')}
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreate}
                                style={{ background: '#1a6b3c' }}
                            >
                                {t('actions.ajouter')}
                            </Button>
                        </Space>
                    )}
                />

                {/* ── Filtres ── */}
                <div
                    style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '16px 20px',
                        marginBottom: 16,
                    }}
                >
                    <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} sm={24} md={9}>
                            <Input
                                prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                                placeholder={t('filtres.recherche')}
                                allowClear
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder={t('filtres.module')}
                                allowClear
                                style={{ width: '100%' }}
                                value={filterModule}
                                onChange={setFilterModule}
                            >
                                {modules.map((m) => (
                                    <Option key={m.moduleTrackingId} value={m.moduleTrackingId}>
                                        {m.nomModule}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder={t('filtres.action')}
                                allowClear
                                style={{ width: '100%' }}
                                value={filterAction}
                                onChange={setFilterAction}
                            >
                                {ACTIONS.map((a) => (
                                    <Option key={a} value={a}>
                                        <Tag color={ACTION_COLORS[a]} style={{ marginRight: 6 }}>
                                            {ACTION_LABELS[a]}
                                        </Tag>
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={3} style={{ textAlign: 'right' }}>
                            <Tooltip title={t('filtres.reinitialiser')}>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => { setSearch(''); setFilterModule(null); setFilterAction(null); }}
                                />
                            </Tooltip>
                        </Col>
                    </Row>
                </div>

                {/* ── Compteur ── */}
                <div style={{ marginBottom: 10 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {filtered.length !== habilitations.length
                            ? t('liste.nbFiltrees', { filtrees: filtered.length, count: habilitations.length }) : t('liste.nbHabilitations', { count: habilitations.length })}
                    </Text>
                </div>

                {/* ── Tableau ── */}
                <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    {/* ── Tableau imbriqué par module ── */}
                    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                        <Table
                            dataSource={groupedData}
                            columns={outerColumns}
                            rowKey="key"
                            loading={loading}
                            size="middle"
                            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50'],
                                    showTotal: (total) => t('liste.nbHabilitations', { count: total }) }}
                            expandable={{
                                defaultExpandAllRows: true,
                                expandedRowRender: (record) => (
                                    <Table
                                        dataSource={record.habilitations}
                                        columns={innerColumns}
                                        rowKey="habilitationTrackingId"
                                        size="small"
                                        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50'],
                                    showTotal: (total) => t('liste.nbHabilitations', { count: total }) }}
                                        scroll={{ x: 800 }}
                                        style={{ margin: '0 0 8px 0' }}
                                    />
                                ),
                                rowExpandable: (record) => record.habilitations.length > 0,
                            }}
                        />
                    </div>
                </div>

                {/* ── Modal Création / Édition ── */}
                <CBModal
                    title={
                        <Space>
                            {modalMode === 'create' ? <PlusOutlined style={{ color: '#1a6b3c' }} /> : <EditOutlined style={{ color: '#1a6b3c' }} />}
                            {modalMode === 'create' ? t('modale.titreCreation') : t('modale.titreEdition')}
                        </Space>
                    }
                    open={modalOpen}
                    onCancel={() => setModalOpen(false)}
                    onOk={handleSave}
                    okText={modalMode === 'create' ? t('modale.creer') : t('modale.enregistrer')}
                    cancelText={t('modale.annuler')}
                    confirmLoading={saving}
                    okButtonProps={{ style: { background: '#1a6b3c' } }}
                    width={560}
                    destroyOnClose
                >
                    <Divider style={{ margin: '12px 0 20px' }} />
                    <Form form={form} layout="vertical" requiredMark="optional">
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="libelleHabilitation"
                                    label={t('modale.libelle')}
                                    rules={[{ required: true, message: t('modale.libelleObligatoire') }]}
                                >
                                    <Input placeholder={t('modale.libelleExemple')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="description" label={t('modale.description')}>
                                    <Input.TextArea rows={2} placeholder={t('modale.descriptionExemple')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="action"
                                    label={t('modale.action')}
                                    rules={[{ required: true, message: t('modale.actionObligatoire') }]}
                                >
                                    <Select placeholder={t('modale.selectionner')}>
                                        {ACTIONS.map((a) => (
                                            <Option key={a} value={a}>
                                                <Tag color={ACTION_COLORS[a]}>{ACTION_LABELS[a]}</Tag>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="typeAcces"
                                    label={t('modale.typeAcces')}
                                    rules={[{ required: true, message: t('modale.typeAccesObligatoire') }]}
                                >
                                    <Select placeholder={t('modale.selectionner')}>
                                        {TYPE_ACCES.map((t) => (
                                            <Option key={t} value={t}>
                                                <Tag color={TYPE_ACCES_COLORS[t]}>{TYPE_ACCES_LABELS[t]}</Tag>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="moduleTrackingId"
                                    label={t('modale.module')}
                                    rules={[{ required: true, message: t('modale.moduleObligatoire') }]}
                                >
                                    <Select
                                        placeholder={t('modale.selectionnerModule')}
                                        showSearch
                                        filterOption={filtreSansAccents}
                                        optionFilterProp="children"
                                    >
                                        {modules.map((m) => (
                                            <Option key={m.moduleTrackingId} value={m.moduleTrackingId}>
                                                {m.nomModule}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </CBModal>

                {/* ── Modal Import ── */}
                <ImportExcelModal
                    open={importOpen}
                    onClose={() => setImportOpen(false)}
                    onSuccess={handleImportSuccess}
                />
            </div>
        </Layout>
    );
}